import { ProcessingImage, cropImage } from '@jax-data-science/sci-image-visualizer';

import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';

/**
 * Client-side **Slide Crop** engine — the pipeline adapter for the library's
 * generic {@link cropImage} utility.
 *
 * Browser-side counterpart of the JIT server `crop` tool (Slide Cropping): it
 * extracts a rectangular sub-image and, optionally, downsamples it by a
 * power-of-two resolution level (the same `cropResolution` knob the server tool
 * exposes; 0 = full resolution). All the actual crop/downsample work lives in
 * the visualization library (`cropImage`); this class is just the thin
 * `ProcessingEngine` registration + parameter metadata for the pipeline.
 */
const CROP_OP_ID = 'client-slide-crop';

export class ClientCropEngine implements ProcessingEngine {
  readonly id = 'client-crop';
  readonly name = 'Slide Crop (client)';
  readonly operations: OperationDescriptor[] = [
    {
      id: CROP_OP_ID,
      name: 'Slide Crop',
      engineId: 'client-crop',
      category: 'transform',
      runsOn: 'client',
      description:
        'Crop a rectangular region from the image (browser-side counterpart of ' +
        'the JIT server Slide Cropping tool). Coordinates are in source pixels ' +
        'and clamped to the image; an optional resolution level downsamples the ' +
        'crop by 2^level (0 = full resolution).',
      params: [
        { id: 'x', label: 'X', type: 'number', defaultValue: 0, min: 0, max: 100000, step: 1,
          tooltip: 'Left edge of the crop, in source pixels (clamped to the image).' },
        { id: 'y', label: 'Y', type: 'number', defaultValue: 0, min: 0, max: 100000, step: 1,
          tooltip: 'Top edge of the crop, in source pixels (clamped to the image).' },
        { id: 'width', label: 'Width', type: 'number', defaultValue: 512, min: 1, max: 100000, step: 1,
          tooltip: 'Crop width in source pixels (clamped to the image bounds).' },
        { id: 'height', label: 'Height', type: 'number', defaultValue: 512, min: 1, max: 100000, step: 1,
          tooltip: 'Crop height in source pixels (clamped to the image bounds).' },
        { id: 'cropResolution', label: 'Resolution (0=full)', type: 'number', defaultValue: 0, min: 0, max: 10, step: 1,
          tooltip: 'Downsample the crop by 2^level. 0 = full resolution, higher = more downsampled. ' +
            'Mirrors the JIT server Slide Cropping cropResolution knob.' },
      ],
    },
  ];

  private _initialized = false;

  isInitialized(): boolean {
    return this._initialized;
  }

  async initialize(): Promise<void> {
    this._initialized = true;
  }

  async execute(
    _operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>,
  ): Promise<ProcessingImage> {
    if (!this._initialized) await this.initialize();
    return cropImage(input, {
      x: Number(params.x ?? 0),
      y: Number(params.y ?? 0),
      width: Number(params.width ?? input.width),
      height: Number(params.height ?? input.height),
      level: Math.max(0, Math.floor(Number(params.cropResolution ?? 0))),
    });
  }

  dispose(): void {
    /* no resources to release */
  }
}
